// Cliente del backend propio. El front NUNCA llama a openfootball directo:
// siempre pasa por estos endpoints (proxy + cache).
//
// En dev, package.json "proxy" redirige /api → http://localhost:8080.
// En prod, configurar REACT_APP_API_BASE.

const BASE = process.env.REACT_APP_API_BASE ?? '';

// Lectura síncrona del cache local (para mostrar al instante en la 2da navegación).
export function cacheRead<T>(path: string): T | null {
  try {
    const raw = localStorage.getItem('m26:' + path);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

// Cache offline en localStorage: red primero; si la red falla, sirve la última copia.
async function get<T>(path: string): Promise<T> {
  const ckey = 'm26:' + path;
  try {
    const res = await fetch(`${BASE}/api${path}`);
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error((body as { error?: string }).error ?? `Error ${res.status}`);
    }
    const json = (await res.json()) as T;
    try {
      localStorage.setItem(ckey, JSON.stringify(json));
    } catch {
      /* sin espacio: ignorar */
    }
    return json;
  } catch (err) {
    if (err instanceof TypeError) {
      // Falla de RED (no de API): servir copia local si existe.
      const raw = localStorage.getItem(ckey);
      if (raw) return JSON.parse(raw) as T;
    }
    throw err;
  }
}

// --- Tipos (espejo del contrato del backend) ---

export type MatchStatus = 'finished' | 'live' | 'upcoming';

export interface TeamRef {
  name: string;
  placeholder: boolean; // true = "2A", "W74" (placeholder de llave)
  resolved: string | null; // selección real (proyectada o confirmada), o null
  projected: boolean; // true = proyección desde un grupo aún en juego (puede cambiar)
  label: string | null; // texto del placeholder: "1º Grupo J", "Mejor 3º (A/B/C/D/F)"
}

export interface Goal {
  name: string;
  minute: string | null;
  penalty: boolean;
  owngoal: boolean;
}

export interface Score {
  ft: [number, number];
  ht: [number, number] | null;
}

export interface Stage {
  key: string; // 'r32' | 'r16' | 'qf' | 'sf' | 'third' | 'final'
  order: number;
  label: string;
}

export interface Match {
  id: number;
  phase: 'group' | 'knockout';
  round: string | null;
  stage: Stage | null;
  group: string | null; // "A".."L"
  num: number | null;
  date: string | null;
  time: string | null;
  kickoffUtc: string | null;
  ground: string | null;
  team1: TeamRef;
  team2: TeamRef;
  score: Score | null;
  status: MatchStatus;
  goals1: Goal[];
  goals2: Goal[];
}

export interface StandingRow {
  position: number;
  team: string;
  pj: number;
  g: number;
  e: number;
  p: number;
  gf: number;
  gc: number;
  dg: number;
  pts: number;
}

export interface GroupData {
  group: string;
  table: StandingRow[];
  matches: Match[];
}

export interface BracketStage {
  key: string;
  label: string;
  order: number;
  matches: Match[];
}

export interface Tournament {
  name: string;
  host: string;
  total: number;
  played: number;
  remaining: number;
  phase: 'group' | 'knockout';
  nextMatch: Match | null;
}

export interface Scorer {
  position: number;
  name: string;
  team: string;
  goals: number;
  penalties: number;
}

export interface MatchSummary {
  id: number;
  team1: string;
  team2: string;
  ft: [number, number];
  diff: number;
  total: number;
  round: string | null;
}

export interface HatTrick {
  player: string;
  team: string;
  goals: number;
  matchId: number;
}

export interface TournamentStats {
  played: number;
  goals: number;
  avg: number;
  penalties: number;
  ownGoals: number;
  scorelessDraws: number;
  biggestWin: MatchSummary | null;
  mostGoals: MatchSummary | null;
  hatTricks: HatTrick[];
  goalsByMinute: Record<string, number>;
}

export interface ThirdRow extends StandingRow {
  group: string;
  rank: number;
  qualified: boolean;
}

export interface TeamListItem {
  name: string;
  group: string;
}

export interface TeamDetail {
  team: string;
  group: string | null;
  standing: StandingRow | null;
  matches: Match[];
}

export const api = {
  tournament: () => get<Tournament>('/tournament'),
  today: () => get<{ live: Match[]; upcoming: Match[]; recent: Match[] }>('/today'),
  fixtures: () => get<{ matches: Match[] }>('/fixtures'),
  match: (id: number | string) => get<{ match: Match }>(`/match/${id}`),
  groups: () => get<{ groups: GroupData[] }>('/groups'),
  bracket: () => get<{ stages: BracketStage[] }>('/bracket'),
  teams: () => get<{ teams: TeamListItem[] }>('/teams'),
  team: (name: string) => get<TeamDetail>(`/team/${encodeURIComponent(name)}`),
  scorers: () => get<{ scorers: Scorer[] }>('/scorers'),
  stats: () => get<{ stats: TournamentStats }>('/stats'),
  thirds: () => get<{ projected: boolean; thirds: ThirdRow[] }>('/thirds'),
};
